import { InfoCard } from "@src/components/infoCard/InfoCard";
import { NavButton } from "@src/components/navButton/NavButton";
import "./politiquementExposee.scss";
import {
  Form,
  InputButtonGroup,
  InputDate,
  InputText,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormRow,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { Stepbar } from "@src/components/stepbar/Stepbar";
import { MicroStep, StepProps } from "@src/Steps/step/Steps";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { OuiNonOptions } from "@src/utils";
import { useEffect } from "react";

const schema = zod
  .object({
    assurePPE: zod.boolean({ required_error: "Champ requis" }).optional(),
    assurePPEFonction: zod.string().optional(),
    assurePPECessation: zod.date().optional(),
    prochePPE: zod.boolean({ required_error: "Champ requis" }).optional(),
    prochePPEFonction: zod.string().optional(),
    prochePPECessation: zod.date().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.assurePPE) {
      if (!data.assurePPEFonction) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ["assurePPEFonction"],
          message: "Champ requis",
        });
      }
    }
    if (data.prochePPE) {
      if (!data.prochePPEFonction) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ["prochePPEFonction"],
          message: "Champ requis",
        });
      }
    }
  });

export type PolitiquementExposeeData = zod.infer<typeof schema>;
type PolitiquementExposeeProps = StepProps<PolitiquementExposeeData>;

export function PolitiquementExposee({
  changeStep,
  data,
  setData,
}: PolitiquementExposeeProps) {
  const formConfig = useFormConfig<typeof schema, PolitiquementExposeeData>({
    schema,
    defaultValues: data,
    mode: "onChange",
  });

  const handleSubmit = (submittedData: PolitiquementExposeeData) => {
    setData({
      ...data,
      ...submittedData,
    });
    changeStep("BENEFICIAIRES", "forward");
  };

  const assurePPE = formConfig.watch("assurePPE");
  const prochePPE = formConfig.watch("prochePPE");

  useEffect(() => {
    if (!assurePPE) {
      formConfig.setValue("assurePPEFonction", "");
      formConfig.setValue("assurePPECessation", undefined);
    }
  }, [assurePPE, formConfig]);

  useEffect(() => {
    if (!prochePPE) {
      formConfig.setValue("prochePPEFonction", "");
      formConfig.setValue("prochePPECessation", undefined);
    }
  }, [prochePPE, formConfig]);

  return (
    <div className="politiquement-exposee-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid step__grid--padding-bottom">
          <Stepbar activeMicroStep={MicroStep.PersonalInformation} />
          <NavButton
            onClick={() => {
              changeStep("NUM_SECU", "backward");
            }}
          >
            Précédent
          </NavButton>
          <Form
            className="politiquement-exposee-step__form"
            formConfig={formConfig}
            onSubmit={handleSubmit}
          >
            <FormGrid>
              <FormRow>
                <FormField>
                  <InputButtonGroup<PolitiquementExposeeData, boolean>
                    label={
                      <>
                        Êtes-vous{" "}
                        <Emphasis>
                          une personne politiquement exposée ?
                        </Emphasis>{" "}
                      </>
                    }
                    variant="outline"
                    size="large"
                    name="assurePPE"
                    required
                    options={OuiNonOptions}
                  />
                </FormField>
              </FormRow>
              {assurePPE && (
                <FormRow>
                  <FormField>
                    <InputText
                      name="assurePPEFonction"
                      required
                      label="Fonction"
                    />
                  </FormField>
                  <FormField>
                    <InputDate
                      name="assurePPECessation"
                      required
                      label="Date de cessation de fonction"
                    />
                  </FormField>
                </FormRow>
              )}
              <FormRow>
                <FormField>
                  <InputButtonGroup<PolitiquementExposeeData, boolean>
                    label={
                      <>
                        Une personne de votre famille ou de votre entourage
                        proches est-elle{" "}
                        <Emphasis>
                          une personne politiquement exposée ?
                        </Emphasis>
                      </>
                    }
                    variant="outline"
                    size="large"
                    name="prochePPE"
                    required
                    options={OuiNonOptions}
                  />
                </FormField>
              </FormRow>
              {prochePPE && (
                <FormRow>
                  <FormField>
                    <InputText
                      name="prochePPEFonction"
                      required
                      label="Fonction"
                    />
                  </FormField>
                  <FormField>
                    <InputDate
                      name="prochePPECessation"
                      required
                      label="Date de cessation de fonction"
                    />
                  </FormField>
                </FormRow>
              )}
              <FormRow>
                <FormField>
                  <InfoCard>
                    <div>
                      <p>
                        Personnes Politiquement Exposées <br />
                        Conformément aux dispositions de l’article R. 561-18 du
                        Code monétaire et financier, une personne politiquement
                        exposée est une personne qui exerce ou a cessé
                        d&apos;exercer depuis moins d&apos;un an l&apos;une des
                        fonctions suivantes : <br />
                        1° Chef d&apos;Etat, chef de gouvernement, membre
                        d&apos;un gouvernement national ou de la Commission
                        européenne ; <br />
                        2° Membre d&apos;une assemblée parlementaire nationale
                        ou du Parlement européen, membre de l&apos;organe
                        dirigeant d&apos;un parti ou groupement politique soumis
                        aux dispositions de la loi n° 88-227 du 11 mars 1988 ou
                        d&apos;un parti ou groupement politique étranger ;<br />
                        3° Membre d&apos;une cour suprême, d&apos;une cour
                        constitutionnelle ou d&apos;une autre haute juridiction
                        dont les décisions ne sont pas, sauf circonstances
                        exceptionnelles, susceptibles de recours ;<br />
                        4° Membre d&apos;une cour des comptes ;<br />
                        5° Dirigeant ou membre de l&apos;organe de direction
                        d&apos;une banque centrale ;<br />
                        6° Ambassadeur ou chargé d&apos;affaires ;<br />
                        7° Officier général ou officier supérieur assurant le
                        commandement d&apos;une armée ;<br />
                        8° Membre d&apos;un organe d&apos;administration, de
                        direction ou de surveillance d&apos;une entreprise
                        publique ;<br />
                        9° Directeur, directeur adjoint, membres du conseil
                        d&apos;une organisation internationale créée par un
                        traité, ou une personne qui occupe une position
                        équivalente en son sein.
                      </p>
                    </div>
                  </InfoCard>
                </FormField>
              </FormRow>
              <FormAction>
                <Button
                  type="submit"
                  disabled={
                    assurePPE === undefined ||
                    prochePPE === undefined ||
                    !formConfig.formState.isValid ||
                    formConfig.formState.isSubmitting ||
                    formConfig.formState.isSubmitSuccessful
                  }
                >
                  Suivant
                </Button>
              </FormAction>
            </FormGrid>
          </Form>
        </div>
      </div>
    </div>
  );
}
